import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Form as BaseForm, Formik, FormikHelpers } from "formik";
import styled from "styled-components";

import { API } from "@api";
import { IStakeholder } from "@api/stakeholders";
import {
  ErrorMessage,
  Modal,
  NumberField,
  SelectField,
  TextField,
} from "@components";
import { TUpdateOnlineLotFormValues } from "@components/forms/UpdateLotForm/UpdateOnlineLotForm";
import { useGlobalContext } from "@contexts/GlobalContext";
import { useI18n } from "@i18n";
import { device } from "@styles/breakpoints";
import { IAuction, ICountry, IError, Nullable } from "@utils";
import { Button } from "../../Button";
import { INITIAL_VALUES } from "./initialValues";
import {
  MAX_RESERVE_PRICE_VALUE,
  MIN_LOT_VALUE,
  MIN_RESERVE_PRICE_VALUE,
  VALIDATION_SCHEMA,
} from "./validationSchema";

export type TUpdateLotFormValues = typeof INITIAL_VALUES;

type Props = {
  initialValues: TUpdateLotFormValues;
  onClose: () => void;
  onRetry: () => void;
  error: Nullable<IError>;
  loading?: boolean;
  onSubmit: (
    values: TUpdateLotFormValues & TUpdateOnlineLotFormValues,
    helpers: FormikHelpers<TUpdateLotFormValues>
  ) => Promise<void>;
  auction: IAuction;
};

const Form = styled(BaseForm)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.tripleMargin};

  & > *:not(:last-child) {
    margin-right: ${({ theme }) => theme.tripleMargin};
  }

  @media ${device.tablet} {
    flex-direction: column;

    & > *:not(:last-child) {
      margin-right: 0;
    }
  }
`;
const StakeholdersContainer = styled.div`
  padding-bottom: 14px;
  width: 100%;
`;

export const UpdateLotForm: FC<Props> = ({
  initialValues,
  loading,
  error,
  onRetry,
  onSubmit,
  onClose,
  auction,
}) => {
  const {
    waffles: {
      switches: { stakeholder },
    },
  } = useGlobalContext();

  const { t } = useI18n();
  const [stakeholders, setStakeholders] = useState<Array<IStakeholder>>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);

  useEffect(() => {
    async function getStakeholders(id: number) {
      try {
        const stakeholders = await API.stakeholders.getAuctionStakeholders(id);
        setStakeholders(stakeholders.results);
      } catch (_) {
        setStakeholders([]);
      }
    }

    async function getCountries() {
      try {
        const countries = await API.users.countries();
        setCountries(countries);
      } catch (_) {
        setCountries([]);
      }
    }

    getStakeholders(auction.id);
    getCountries();
  }, []);

  const handleSubmit = useCallback(
    async (values, helpers) => {
      const newValues: Record<string, any> = {};
      Object.entries(initialValues).forEach(([key, value]) => {
        if (values[key] === value) return;
        newValues[key] = values[key];
      });

      helpers.setSubmitting(true);
      await onSubmit(newValues as TUpdateOnlineLotFormValues, helpers);
      helpers.setSubmitting(false);
    },
    [onSubmit, initialValues]
  );

  const formInitalValues = useMemo(() => {
    return {
      ...INITIAL_VALUES,
      ...initialValues,
    };
  }, [initialValues]);

  return (
    <Formik<TUpdateLotFormValues>
      initialValues={formInitalValues}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isValid, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Modal
            width="50%"
            title={t("lotForm.updateTitle")}
            actions={
              <>
                <Button
                  onClick={() => {
                    onClose();
                  }}
                  bordered
                >
                  {t<string>("lotForm.cancel")}
                </Button>
                {!error && (
                  <Button
                    type="submit"
                    disabled={!isValid}
                    onClick={() => null}
                    loading={loading}
                  >
                    {t<string>("lotForm.update")}
                  </Button>
                )}
              </>
            }
            onClose={onClose}
          >
            {error ? (
              <ErrorMessage error={error} onRetry={onRetry} />
            ) : (
              <>
                <Row>
                  <NumberField
                    name="lot"
                    label={t("lotForm.lot.label")}
                    placeholder={t("lotForm.lot.placeholder")}
                    min={MIN_LOT_VALUE}
                    required
                  />
                  <NumberField
                    name="reservePrice"
                    label={t("lotForm.reservePrice.label", {
                      currency: auction.currency.code,
                    })}
                    placeholder={t("lotForm.reservePrice.placeholder", {
                      currency: auction.currency.code,
                    })}
                    min={MIN_RESERVE_PRICE_VALUE}
                    max={MAX_RESERVE_PRICE_VALUE}
                    step="0.01"
                    required
                  />
                </Row>
                <Row>
                  <TextField
                    name="name"
                    label={t("lotForm.name.label")}
                    placeholder={t("lotForm.name.placeholder")}
                    required
                  />
                </Row>
                {stakeholder ? (
                  <Row>
                    <StakeholdersContainer>
                      <SelectField
                        name="stakeholder"
                        label={t("lotForm.stakeholder.label")}
                        placeholder={t("lotForm.stakeholder.placeholder")}
                        options={stakeholders.map((item) => {
                          const country = countries.find(
                            (country) =>
                              country.geoname_id === Number(item.country)
                          );
                          return {
                            label: `${country?.name} ${item.agency}`,
                            value: item.id,
                          };
                        })}
                      />
                    </StakeholdersContainer>
                  </Row>
                ) : null}
              </>
            )}
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
